import { useState, useEffect, useMemo, useLayoutEffect, useRef, useSyncExternalStore, useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";
import { getDisplayName, getSenderFromMetadata } from "@/lib/utils/display-name";
import { currentUser, workspaceMembers as dummyWorkspaceMembers } from "@/data/dummy";
import { useWorkspaceMembers } from "@/lib/hooks/use-workspace-members";
import { resolveOwnerDisplayName, resolveAdditionalOwnerNames, mapOwnerNamesToIds } from "@/lib/utils/deal-owners";
import { resolveDealPipeline } from "@/lib/utils/deal-pipeline";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DollarSign, Calendar, Building2, Paperclip, Star, Users, Video, Mail, FileText, Pencil, Check, X, Sparkles, TrendingUp, StickyNote, ArrowLeft, MoreHorizontal, ListPlus, Trash2, ChevronDown, ChevronRight, Package, CheckSquare, Lock, Copy, Activity, List as ListIcon, Layers, Clock, UserCircle2, Gauge, Phone } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AddToListDialog from "@/components/shared/AddToListDialog";
import ConfirmDeleteDialog from "@/components/shared/ConfirmDeleteDialog";
import { MeetingsTable, EmailsTable, FilesTable, NotesTable, PeopleTable, TasksTable } from "@/components/shared/DrillDownTable";
import RecordCallsTab from "@/components/shared/RecordCallsTab";
import type { NoteItem } from "@/components/shared/SheetDrillDown";
import { EmailDrillDown, FileDrillDown, DealDrillDown, NoteDrillDown } from "@/components/shared/SheetDrillDown";
import PersonDetailSheet from "@/components/shared/PersonDetailSheet";
import CompanyDetailSheet from "@/components/shared/CompanyDetailSheet";
import TaskDetailSheet from "@/components/shared/TaskDetailSheet";
import MeetingIntelligenceSheet from "@/components/shared/MeetingIntelligenceSheet";
import MeetingBriefingSheet from "@/components/shared/MeetingBriefingSheet";
import LinkedText from "@/components/shared/LinkedText";
import type { NavigationItem } from "@/components/shared/SheetDrillDown";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import CompanyLogo from "@/components/shared/CompanyLogo";
import EntityListBadges from "@/components/shared/EntityListBadges";
import MoreFieldsPanel, { MoreFieldRow, type MorePanelField } from "@/components/shared/MoreFieldsPanel";
import EditableDetailField from "@/components/shared/EditableDetailField";
import UserAvatar from "@/components/shared/Avatar";
import MultiContactCell, { type ContactCandidate } from "@/components/shared/MultiContactCell";
import EditableContacts from "@/components/shared/EditableContacts";
import { useEntityByName } from "@/lib/hooks/use-entity-by-name";
import { cn } from "@/lib/utils";
import { people as allPeople, companies as allCompanies } from "@/data/dummy";
import { useFavorites } from "@/contexts/FavoritesContext";
import { usePipelines } from "@/lib/hooks/use-pipelines";
import { format } from "date-fns";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useFormatters } from "@/lib/i18n/useFormatters";
import { useGetDealLazyQuery, DdbEngagementType, NoteStatusEnum } from "@/lib/graphql/types-and-hooks";
import { useTaskMutations } from "@/lib/hooks/use-task-mutations";
import { useDeleteNote } from "@/lib/hooks/use-delete-note";
import { isLovablePreview } from "@/lib/utils/environment";
import { dealEditStore } from "@/lib/utils/deal-edit-store";
import { useEngagementPrivacy } from "@/lib/hooks/use-engagement-privacy";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { useDealEngagements } from "@/lib/hooks/use-deal-engagements";
import { useDealMutations } from "@/lib/hooks/use-deal-mutations";
import { resolveSheetDeleteAction } from "@/lib/utils/sheet-delete";
import { useDealFiles } from "@/lib/hooks/use-deal-files";
import { useUploadFile } from "@/lib/hooks/use-upload-file";
import { useDealNotes } from "@/lib/hooks/use-deal-notes";
import { useTasks as useDummyTasks } from "@/contexts/TasksContext";
import { useTasks as useBackendTasks } from "@/lib/hooks/use-tasks";
import { useFeatureFlag } from "@/lib/feature-flags";
import { FEATURE_FLAGS } from "@/lib/feature-flags/registry";
import { useIsReadOnly } from "@/lib/rbac";
import NoteEditor from "@/components/shared/NoteEditor";
import DealProductsPicker from "@/components/shared/DealProductsPicker";
import { useDealLineItems } from "@/lib/hooks/use-deal-line-items";
import { useStickyState } from "@/hooks/useStickyState";
import { useWorkspaceLocalStorage } from "@/hooks/useWorkspaceLocalStorage";
import { useActiveGridColumnPrefs } from "@/hooks/useActiveGridColumnPrefs";
import { useDebouncedDealUpdate } from "@/lib/hooks/use-debounced-deal-update";
import type { ColumnDef } from "@/hooks/useTableColumns";
import type { PartialBlock } from "@blocknote/core";
import { Textarea } from "@/components/ui/textarea";
import { getDummyMeddpiccValue } from "@/data/dummyMeddpicc";
import {
  METHODOLOGY_FIELDS,
  METHODOLOGY_LABELS,
  methodologyFieldDescription,
  methodologyFieldLabel,
  isMethodologyTemplate,
  resolveMethodologyCrmName,
  summaryTemplateStorageKey,
  type MethodologyField,
} from "@/lib/methodology-fields";
import { useDealCustomFields } from "@/lib/hooks/use-deal-custom-fields";
import CustomColumnCell from "@/components/shared/CustomColumnCell";
import type { ColumnType } from "@/hooks/useTableColumns";
import { FORECAST_CATEGORIES } from "@/lib/utils/forecast-categories";
import { Checkbox } from "@/components/ui/checkbox";
import { isCheckboxChecked } from "@/lib/utils/custom-column";
import { useStageLabel } from "@/lib/i18n/stage-label";

type SummarySection = {
  title: string;
  bullets?: (string | { text: string })[];
  body?: string;
};

function ProductsCollapsibleSection({ count, children }: { count: number; children: React.ReactNode }) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useStickyState<boolean>("deal-products-collapsed", false);
  return (
    <div className="px-6 pt-4">
      <button
        type="button"
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between gap-2 py-1.5 text-sm font-medium text-foreground hover:text-accent transition-colors"
        aria-expanded={!collapsed}
      >
        <span className="flex items-center gap-2">
          <Package className="h-3.5 w-3.5 text-muted-foreground" />
          {t("fields.deals.products")}
          {count > 0 && (
            <span className="text-xs text-muted-foreground font-normal">({count})</span>
          )}
        </span>
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", collapsed && "-rotate-90")} />
      </button>
      {!collapsed && <div className="pt-2">{children}</div>}
    </div>
  );
}

function MethodologySection({
  templateId,
  dealId,
  workspaceId,
  isReadOnly,
  isPreview,
  customProperties,
  onFieldUpdate,
}: {
  templateId: keyof typeof METHODOLOGY_FIELDS;
  dealId: string;
  workspaceId: string | null | undefined;
  isReadOnly: boolean;
  isPreview: boolean;
  customProperties: Record<string, any>;
  onFieldUpdate?: (field: string, value: string) => void;
}) {
  const { t } = useTranslation();
  const wsSuffix = workspaceId ? `:ws:${workspaceId}` : "";
  const [collapsed, setCollapsed] = useStickyState<boolean>(
    `deal-methodology-collapsed:${templateId}${wsSuffix}`,
    false,
  );
  const fields = METHODOLOGY_FIELDS[templateId];
  const label = METHODOLOGY_LABELS[templateId] ?? templateId.toUpperCase();
  const { customFields } = useDealCustomFields({ methodologyScope: true });
  return (
    <div className="px-6 pt-4">
      <button
        type="button"
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between gap-2 py-1.5 text-sm font-medium text-foreground hover:text-accent transition-colors"
        aria-expanded={!collapsed}
      >
        <span className="flex items-center gap-2">
          <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
          {label}
          <span className="text-xs text-muted-foreground font-normal">({fields.length})</span>
        </span>
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", collapsed && "-rotate-90")} />
      </button>
      {!collapsed && (
        <div className="pt-2 space-y-3">
          {fields.map(f => {
            // Map the synthetic methodology key to the workspace's real custom
            // field (e.g. meddpicc_metrics -> metrics_5876_coffee) so we read and
            // write the same field the grid and CRM sync-back use. Fall back to
            // the generic key when there's no matching custom field.
            const crmName = resolveMethodologyCrmName(f, customFields);
            const readKey = crmName ?? f.fieldName;
            const persistKey = `custom_${crmName ?? f.fieldName}`;
            return (
              <MethodologyFieldRow
                key={`${dealId}:${f.fieldName}`}
                field={f}
                dealId={dealId}
                workspaceId={workspaceId}
                isReadOnly={isReadOnly}
                isPreview={isPreview}
                backendValue={customProperties[readKey] != null ? String(customProperties[readKey]) : ""}
                persistKey={persistKey}
                onPersist={onFieldUpdate}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function MethodologyFieldRow({
  field,
  dealId,
  workspaceId,
  isReadOnly,
  isPreview,
  backendValue,
  persistKey,
  onPersist,
  fieldType,
  selectOptions,
}: {
  field: MethodologyField;
  dealId: string;
  workspaceId: string | null | undefined;
  isReadOnly: boolean;
  isPreview: boolean;
  backendValue: string;
  persistKey: string;
  onPersist?: (field: string, value: string) => void;
  // When set (custom deal fields), the row renders the type-appropriate widget
  // (select, checkbox, date, …) instead of a free-text textarea, so a
  // multi-select enforces its options instead of accepting arbitrary text.
  // Hardcoded methodology fields omit these and keep the qualitative textarea.
  fieldType?: string;
  selectOptions?: string[];
}) {
  const { t } = useTranslation();
  const wsSuffix = workspaceId ? `:ws:${workspaceId}` : "";
  // Seed plausible MEDDPICC content for the Gazillion preview workspace so
  // reps can see what populated qualification data looks like.
  const isGazillionMeddpicc =
    workspaceId === "1" && field.fieldName.startsWith("meddpicc_");
  const seed = isGazillionMeddpicc
    ? getDummyMeddpiccValue(dealId, field.fieldName)
    : "";
  // Bump the storage-key version when seeding so stale empty strings from
  // earlier sessions (before the seed existed) don't suppress the seed value.
  const versionSuffix = isGazillionMeddpicc ? ":v2" : "";
  // Preview persists to localStorage so the dummy workspace stays interactive
  // without a backend. Production reads/writes the deal's customProperties —
  // methodology fields are platform=COFFEE custom fields keyed by field_name
  // (see Pattern 48) — debounced upstream via onFieldUpdate → updateCell.
  const [previewValue, setPreviewValue] = useStickyState<string>(
    `deal-methodology:${dealId}:${field.fieldName}${wsSuffix}${versionSuffix}`,
    seed,
  );
  const [draft, setDraft] = useState(backendValue);
  const focusedRef = useRef(false);
  // Pick up the backend value once the deal loads (or an external update lands)
  // without clobbering an in-progress edit.
  useEffect(() => {
    if (!isPreview && !focusedRef.current) setDraft(backendValue);
  }, [backendValue, isPreview]);

  const value = isPreview ? previewValue : draft;
  const handleChange = (next: string) => {
    if (isPreview) {
      setPreviewValue(next);
    } else {
      setDraft(next);
      onPersist?.(persistKey, next);
    }
  };

  // Auto-grow the textarea to fit its content so reps don't have to drag the
  // resize handle. Recomputes on value change and on mount.
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  // Structured custom fields (select/checkbox/date/number/…) render the same
  // widget as the Deals grid via CustomColumnCell, so the value encoding (e.g.
  // "||"-joined multi-select) round-trips between grid and detail sheet and the
  // field enforces its defined options. Free-text and AI fields — and all
  // hardcoded methodology fields — keep the auto-growing textarea.
  const useStructuredWidget = !!fieldType && fieldType !== "text" && fieldType !== "ai";

  const control = useStructuredWidget ? (
    isReadOnly ? (
      fieldType === "checkbox" ? (
        <div className="pt-1.5">
          <Checkbox
            checked={isCheckboxChecked(value)}
            disabled
            aria-readonly
            className="h-4 w-4 cursor-default disabled:opacity-100"
          />
        </div>
      ) : (
        <div className="text-xs pt-1.5 text-foreground">{value ? value.split("||").join(", ") : "-"}</div>
      )
    ) : (
      <div className="min-w-0">
        <CustomColumnCell
          col={{ key: persistKey, label: field.displayName, type: fieldType as ColumnType, selectOptions }}
          value={value}
          onChange={handleChange}
        />
      </div>
    )
  ) : (
    <Textarea
      ref={textareaRef}
      value={value}
      onChange={e => handleChange(e.target.value)}
      onFocus={() => { focusedRef.current = true; }}
      onBlur={() => { focusedRef.current = false; }}
      disabled={isReadOnly}
      placeholder={isReadOnly ? "-" : `Add ${field.displayName.toLowerCase()}…`}
      rows={1}
      className="min-h-[44px] text-xs resize-none overflow-hidden"
    />
  );

  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 items-start">
      <div className="pt-1.5">
        <div className="text-xs font-medium text-foreground">{methodologyFieldLabel(field, t)}</div>
        {field.description && (
          <div className="text-[10px] text-muted-foreground leading-snug">
            {methodologyFieldDescription(field, t)}
          </div>
        )}
      </div>
      {control}
    </div>
  );
}

// Renders the workspace's ACTUAL deal custom fields (same source as the Deals
// grid) as editable rows. Used for any workspace that has deal custom fields —
// which is what makes MEDDPICC/qualification fields show in the detail sheet
// even when the summary template is a custom template rather than a built-in
// methodology keyword. Reads/writes the real crm_name so edits sync to the CRM.
function DealCustomFieldsSection({
  customFields,
  dealId,
  workspaceId,
  isReadOnly,
  isPreview,
  customProperties,
  onFieldUpdate,
}: {
  customFields: ReadonlyArray<{ crmName: string; label: string; type?: string; selectOptions?: string[] }>;
  dealId: string;
  workspaceId: string | null | undefined;
  isReadOnly: boolean;
  isPreview: boolean;
  customProperties: Record<string, any>;
  onFieldUpdate?: (field: string, value: string) => void;
}) {
  const { t } = useTranslation();
  const wsSuffix = workspaceId ? `:ws:${workspaceId}` : "";
  const [collapsed, setCollapsed] = useStickyState<boolean>(
    `deal-custom-fields-collapsed${wsSuffix}`,
    false,
  );
  return (
    <div className="px-6 pt-4">
      <button
        type="button"
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between gap-2 py-1.5 text-sm font-medium text-foreground hover:text-accent transition-colors"
        aria-expanded={!collapsed}
      >
        <span className="flex items-center gap-2">
          <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
          {t("detailSheets.dealFields")}
          <span className="text-xs text-muted-foreground font-normal">({customFields.length})</span>
        </span>
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", collapsed && "-rotate-90")} />
      </button>
      {!collapsed && (
        <div className="pt-2 space-y-3">
          {customFields.map(cf => (
            <MethodologyFieldRow
              key={`${dealId}:${cf.crmName}`}
              field={{ fieldName: cf.crmName, displayName: cf.label, description: "" }}
              dealId={dealId}
              workspaceId={workspaceId}
              isReadOnly={isReadOnly}
              isPreview={isPreview}
              backendValue={customProperties[cf.crmName] != null ? String(customProperties[cf.crmName]) : ""}
              persistKey={`custom_${cf.crmName}`}
              onPersist={onFieldUpdate}
              fieldType={cf.type}
              selectOptions={cf.selectOptions}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MethodologyGate({
  dealId,
  workspaceId,
  isReadOnly,
  isPreview,
  customProperties,
  onFieldUpdate,
}: {
  dealId: string;
  workspaceId: string | null | undefined;
  isReadOnly: boolean;
  isPreview: boolean;
  customProperties: Record<string, any>;
  onFieldUpdate?: (field: string, value: string) => void;
}) {
  // methodologyScope: the drill-down mirrors the grid — only the picked
  // methodology's fields render here.
  const { customFields } = useDealCustomFields({ methodologyScope: true });
  const [summaryTemplate] = useStickyState<string>(
    summaryTemplateStorageKey(workspaceId),
    workspaceId === "1" ? "meddpicc" : "general",
  );
  // Prefer the workspace's real deal custom fields (same source as the grid) so
  // the section shows for ANY workspace — not only those whose summary template
  // is a built-in methodology keyword (e.g. ws5876 uses a custom template, so the
  // old isMethodologyTemplate gate hid it entirely). Fall back to the hardcoded
  // methodology card only in Lovable preview, which has no backend custom
  // fields. Production must never take that path: methodology scoping can now
  // legitimately empty the list (e.g. General pick, or while scope resolves),
  // and the hardcoded card would read/write synthetic keys like
  // `custom_meddpicc_metrics` that no workspace stores.
  if (customFields.length > 0) {
    return (
      <DealCustomFieldsSection
        customFields={customFields}
        dealId={dealId}
        workspaceId={workspaceId}
        isReadOnly={isReadOnly}
        isPreview={isPreview}
        customProperties={customProperties}
        onFieldUpdate={onFieldUpdate}
      />
    );
  }
  if (!isPreview || !isMethodologyTemplate(summaryTemplate)) return null;
  return (
    <MethodologySection
      templateId={summaryTemplate}
      dealId={dealId}
      workspaceId={workspaceId}
      isReadOnly={isReadOnly}
      isPreview={isPreview}
      customProperties={customProperties}
      onFieldUpdate={onFieldUpdate}
    />
  );
}

function summaryToBlocks(summary: SummarySection[]): PartialBlock[] {
  const blocks: PartialBlock[] = [];

  summary.forEach((section) => {
    if (!section || (!section.bullets && !section.body)) return;

    blocks.push({
      type: "heading",
      props: { level: 3 },
      content: [{ type: "text" as const, text: section.title, styles: {} }],
    });

    if (Array.isArray(section.bullets)) {
      section.bullets.forEach((bullet) => {
        const text = typeof bullet === 'string' ? bullet : bullet.text;
        blocks.push({
          type: "bulletListItem",
          content: [{ type: "text" as const, text, styles: {} }],
        });
      });
    }

    if (section.body && !section.bullets) {
      blocks.push({
        type: "paragraph",
        content: [{ type: "text" as const, text: section.body, styles: {} }],
      });
    }
  });

  return blocks;
}

interface Deal {
  id: string;
  name: string;
  company?: string;
  companyId?: string; // Backend account id, used to open the company drill-down in production
  stage?: string;
  amount?: number;
  closeDate?: string;
  contacts?: string;
  owner?: string;
  additionalOwners?: string[];
  lastEmail?: string;
  lastMeeting?: string;
  nextMeeting?: string;
  billing?: string;
  /** Set on onboarding "Test" deals — renders self-contained dummy content, no backend fetch. */
  isTestData?: boolean;
}

interface DealDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deal: Deal | null;
  pipelineId?: string; // Show stages from this pipeline (falls back to matching deal's current stage)
  hideOverlay?: boolean; // Hide overlay when opened as a nested/stacked sheet
  onBack?: () => void; // Show back arrow to return to previous sheet
  onFieldUpdate?: (field: string, value: string) => void;
  // Owner edits (primary + additional) are a list, so they can't ride the string-based
  // onFieldUpdate. The parent maps names → member ids and persists ownerId +
  // additionalOwnerIds in one updateDeal. Wired by DealsPage so the grid stays in sync;
  // where it's omitted (global overlay, company sheet) the sheet saves directly.
  onOwnersUpdate?: (owners: string[]) => void;
  // Stage changes carry the stage's externalId so the backend stores the pipeline-correct
  // stage code, not the display name. Critical when moving a deal between pipelines:
  // sending the name makes the backend stage lookup miss and the deal disappears from
  // both pipelines. Falls back to onFieldUpdate("stage", name) when not provided, and with
  // neither (global overlay, company sheet) the sheet saves the stage itself.
  // Returns whether the move stuck (false = backend sync-guard rejected it) so the
  // sheet can revert its optimistic stage override; void is treated as "kept".
  onStageChange?: (stageName: string, stageExternalId: string) => void | Promise<boolean>;
  onDelete?: (id: string) => void; // Override default delete (e.g. optimistic hide + list refetch); omit to delete via useDealMutations
  onClone?: (deal: Deal) => void; // Open clone dialog prefilled from this deal
}

const dummyMeetings = [
  { id: "1", title: "Deal Review & Next Steps", date: "Feb 27, 2026", duration: "45 min", attendees: ["Sarah Chen", currentUser.name] },
  { id: "2", title: "Pricing Discussion", date: "Feb 18, 2026", duration: "30 min", attendees: ["James Wilson", currentUser.name] },
  { id: "3", title: "Technical Requirements", date: "Feb 10, 2026", duration: "60 min", attendees: ["Alex Kim", currentUser.name] },
  { id: "4", title: "Initial Scoping Call", date: "Jan 28, 2026", duration: "30 min", attendees: ["Maria Garcia", currentUser.name, dummyWorkspaceMembers[1].name] },
  { id: "5", title: "Intro Meeting", date: "Jan 15, 2026", duration: "30 min", attendees: ["Sarah Chen", currentUser.name] },
];

const dummyEmails = [
  { id: "1", subject: "Re: Updated Proposal", date: "Feb 28, 2026", from: "Sarah Chen", to: ["Doug"], cc: ["Lisa Park"], direction: "inbound" as const },
  { id: "2", subject: "Updated Proposal — Q2", date: "Feb 25, 2026", from: "Doug" as string, to: ["Sarah Chen"], direction: "outbound" as const },
  { id: "3", subject: "Re: Pricing Breakdown", date: "Feb 19, 2026", from: "Alex Kim", to: ["Doug", "Lisa Park"], direction: "inbound" as const },
  { id: "4", subject: "Pricing Breakdown", date: "Feb 18, 2026", from: "Doug" as string, to: ["Alex Kim"], direction: "outbound" as const },
];

const dummyFiles = [
  { id: "1", name: "Proposal_v2_Final.pdf", size: "3.1 MB", date: "Feb 25, 2026" },
  { id: "2", name: "SOW_Draft.docx", size: "1.2 MB", date: "Feb 18, 2026" },
  { id: "3", name: "Pricing_Schedule.xlsx", size: "450 KB", date: "Feb 18, 2026" },
];

// teamMembers is now derived inside the component from useWorkspaceMembers

export default function DealDetailSheet({ open, onOpenChange, deal, pipelineId, hideOverlay = false, onBack, onFieldUpdate, onOwnersUpdate, onStageChange, onDelete, onClone }: DealDetailSheetProps) {
  const stageLabel = useStageLabel();
  const [activeTab, setActiveTab] = useState("summary");
  const { isFavorite, toggleFavorite } = useFavorites();
  const { pipelines, hasBackendPipelines } = usePipelines();
  const { t } = useTranslation();
  const { formatDate, formatDateTime, formatTime } = useFormatters();
  const { formatAmount } = useCurrency();
  const { members: realWorkspaceMembers } = useWorkspaceMembers();
  const teamMembers = useMemo(() => realWorkspaceMembers.map(m => m.name).sort(), [realWorkspaceMembers]);
  const [navStack, setNavStack] = useState<NavigationItem[]>([]);

  // Nested PersonDetailSheet state
  const [personSheetOpen, setPersonSheetOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<{ id: string; name: string; title?: string; company?: string; email?: string; phone?: string; } | null>(null);

  // CompanyDetailSheet state (stacked sheet)
  const [companySheetOpen, setCompanySheetOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<{ id: string; name: string; industry?: string; description?: string; location?: string; website?: string; employees?: string; revenue?: string; } | null>(null);

  // TaskDetailSheet state (stacked sheet) — lets the task title open the task
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  // Meeting/Briefing intelligence sheet state
  const [meetingIntelligenceOpen, setMeetingIntelligenceOpen] = useState(false);
  const [meetingIntelligenceNote, setMeetingIntelligenceNote] = useState<{ id: string; name: string; company: string; people: string[]; date: string } | null>(null);
  const [meetingIntelligenceTab, setMeetingIntelligenceTab] = useState("summary");
  const [briefingOpen, setBriefingOpen] = useState(false);
  const [briefingMeeting, setBriefingMeeting] = useState<{ id: string; title: string; date: string; time: string; duration: string; type: string; attendees: string[]; link?: string } | null>(null);

  // Editable state. setEditingField is wrapped so read-only users can never
  // enter edit mode — every startEditX function (name, amount, stage,
  // closeDate, owner, billing, etc.) flows through this setter, so gating
  // here covers all of them at once.
  const [editingField, setEditingFieldRaw] = useState<string | null>(null);
  const setEditingField = (next: string | null) => {
    if (next !== null && isReadOnly) return;
    setEditingFieldRaw(next);
  };
  const [editName, setEditName] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editStage, setEditStage] = useState("");
  const [editCloseDate, setEditCloseDate] = useState<Date | undefined>();
  const [editOwner, setEditOwner] = useState("");
  const [localPipelineId, setLocalPipelineId] = useState<string | null>(null);
  const [editBilling, setEditBilling] = useState("");

  // Local overrides for edited values
  const [overrides, setOverrides] = useState<Partial<Deal>>({});

  // Feature flags
  const isReadOnly = useIsReadOnly();
  // Detail-sheet kebab actions are all mutations — hide for read-only.
  const drillDownActionsEnabled = useFeatureFlag(FEATURE_FLAGS.DRILL_DOWN_ACTIONS) && !isReadOnly;
  // HubSpot-synced calls (coffee#4959); hides the tab and skips its query when off.
  const callsTabEnabled = useFeatureFlag(FEATURE_FLAGS.CALLS_TAB);
  const productsEnabled = useFeatureFlag(FEATURE_FLAGS.PRODUCTS);
  const moreFieldsEnabled = useFeatureFlag(FEATURE_FLAGS.DEAL_DETAIL_MORE_FIELDS);

  // Line items for this deal
  const { items: lineItems, setItems: setLineItems } = useDealLineItems(deal?.id, (deal as any)?._deal?.lineItems ?? (deal as any)?.lineItems);
  const [addToListOpen, setAddToListOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const { deleteDeal, updateDeal } = useDealMutations();
  // Resolves display names (meeting attendees, email senders, the company
  // label) to real records — backend search in production, dummy in preview.
  const { resolvePerson, resolveCompany } = useEntityByName();
  const { scheduleUpdate: scheduleDealFieldUpdate } = useDebouncedDealUpdate(500);

  // Stage ID → name map (backend may return IDs or external IDs instead of display names)
  const stageNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of pipelines) {
      for (const stage of p.stages) {
        m.set(stage.name, stage.name);
        m.set(stage.id, stage.name);
        if (stage.externalId) m.set(stage.externalId, stage.name);
      }
    }
    return m;
  }, [pipelines]);

  // Onboarding "Test" deals render self-contained dummy content instead of
  // fetching from the backend (they don't exist there), mirroring the Notes
  // tour step. Treated the same as Lovable preview throughout the sheet.
  const isTestData = !!deal?.isTestData;
  const previewMode = isLovablePreview() || isTestData;

  // Backend data hooks
  const workspaceId = useWorkspaceId();
  const [getDeal, { data: dealData }] = useGetDealLazyQuery({
    fetchPolicy: 'network-only',
  });

  // Backend data hooks for tabs (only fetch when sheet is open)
  const { engagements: rawMeetings, hasMore: hasMeetingMore, loadMore: loadMoreMeetings } = useDealEngagements({
    dealId: deal?.id || '',
    type: DdbEngagementType.Meeting,
    skip: !open || isTestData,
  });
  const { engagements: rawEmailEngagements, hasMore: hasEmailMore, loadMore: loadMoreEmails } = useDealEngagements({
    dealId: deal?.id || '',
    type: DdbEngagementType.Email,
    skip: !open || isTestData,
  });
  const { files: dealFiles, refetch: refetchFiles } = useDealFiles(deal?.id || '', { skip: !open || isTestData });
  const { uploadFile, uploading, uploadProgress } = useUploadFile({
    entityType: 'deal',
    entityId: deal?.id || '',
    onSuccess: () => { refetchFiles(); },
  });
  const { notes: dealNotes, refetch: refetchDealNotes } = useDealNotes(deal?.id || '', { skip: !open || isTestData });
  const { deleteTask } = useTaskMutations();
  const { deleteNote } = useDeleteNote();
  // Preview mode: dummy tasks scope by deal name (see dummy.ts linkedDeals).
  const { tasks: dummyTasks } = useDummyTasks();
  // Production: tasks are notes with type=task; fetch the ones associated with this
  // deal by id (the deal detail sheet previously read the dummy context + matched
  // on deal name, so real tasks never appeared here). Onboarding test deals don't
  // exist in the backend, so skip the fetch and fall through to the dummy branch.
  const dealTaskFilter = useMemo(() => ({ dealId: deal?.id }), [deal?.id]);
  const { tasks: backendDealTasks } = useBackendTasks({
    filter: dealTaskFilter,
    skip: !open || !deal?.id || isTestData,
  });
  const dealTasks = useMemo(() => {
    if (previewMode) {
      if (!deal?.name) return [];
      return dummyTasks.filter(t => t.linkedDeals?.includes(deal.name));
    }
    return backendDealTasks.map(t => ({
      id: t.id!,
      title: t.title || 'Untitled',
      dueDate: t.dueDate || '',
      assignee: t.user?.preferredName || t.user?.givenName || '',
      completed: t.status === NoteStatusEnum.Completed,
    }));
  }, [dummyTasks, backendDealTasks, deal?.name, deal?.id]);

  // Fetch deal details when sheet opens with a backend ID
  useEffect(() => {
    if (open && deal?.id && workspaceId && !previewMode) {
      getDeal({
        variables: {
          id: deal.id,
        },
      });
    }
  }, [open, deal?.id, workspaceId, getDeal]);

  // Transform backend data to Deal format. The lazy query keeps the previous
  // deal's response in `data` while a fetch for the newly selected deal is
  // in flight, so gate on id — otherwise fast switching between deals renders
  // the old deal's fields under the new selection until the response lands.
  const backendDeal = dealData?.deal && dealData.deal.id === deal?.id ? dealData.deal : undefined;
  const useBackendData = !previewMode && !!backendDeal;

  const lastMeetingPrivacy = useEngagementPrivacy(useBackendData ? backendDeal?.lastActivityEngagement : null);
  const nextMeetingPrivacy = useEngagementPrivacy(useBackendData ? backendDeal?.nextStepEngagements?.[0] : null);
  const backendDealTransformed = useMemo((): Partial<Deal> | null => {
    if (!backendDeal) return null;

    const account = backendDeal.accounts?.edges?.[0]?.node;
    // Resolve display names via workspace members (by id) so owner names match the
    // member list exactly — the "Add owner" picker dedupes by name, so a mismatch
    // would let an existing owner reappear as addable.
    const ownerName = resolveOwnerDisplayName(backendDeal.owner, realWorkspaceMembers) || undefined;
    const additionalOwnerNames = resolveAdditionalOwnerNames(backendDeal.additionalOwners, realWorkspaceMembers);

    return {
      name: backendDeal.properties?.name || 'Unknown Deal',
      company: account?.properties?.name || undefined,
      companyId: account?.id || undefined,
      stage: stageNameMap.get(backendDeal.properties?.stage || '') || backendDeal.properties?.stage || undefined,
      amount: backendDeal.properties?.amount != null ? Number(backendDeal.properties.amount) : undefined,
      closeDate: backendDeal.properties?.closeDate
        ? formatDate(backendDeal.properties.closeDate)
        : undefined,
      owner: ownerName || undefined,
      additionalOwners: additionalOwnerNames,
      lastEmail: backendDeal.lastEmail?.startTime
        ? formatDate(backendDeal.lastEmail.startTime)
        : undefined,
      lastMeeting: backendDeal.lastActivityEngagement?.startTime
        ? formatDate(backendDeal.lastActivityEngagement.startTime)
        : undefined,
      nextMeeting: backendDeal.nextStepEngagements?.[0]?.startTime
        ? formatDate(backendDeal.nextStepEngagements[0].startTime)
        : undefined,
    };
  }, [backendDeal, stageNameMap, realWorkspaceMembers]);

  // Parse AI summary JSON into BlockNote blocks
  const summaryBlocks = useMemo(() => {
    if (previewMode) return null;
    if (!backendDeal?.properties?.aiSummary) return null;

    try {
      let rawContent = backendDeal.properties.aiSummary;

      if (typeof rawContent === 'string') {
        rawContent = rawContent.trim();
        const codeBlockMatch = rawContent.match(/^```(?:json)?\s*\n?([\s\S]*?)\n?```$/);
        if (codeBlockMatch) {
          rawContent = codeBlockMatch[1].trim();
        }
      }

      const parsed = typeof rawContent === 'string'
        ? JSON.parse(rawContent)
        : rawContent;

      if (Array.isArray(parsed)) {
        return summaryToBlocks(parsed as SummarySection[]);
      }

      if (parsed?.sections && Array.isArray(parsed.sections)) {
        return summaryToBlocks(parsed.sections as SummarySection[]);
      }

      if (parsed?.relationshipStatus || parsed?.recentContext || parsed?.keyTakeaways) {
        const blocks: PartialBlock[] = [];

        if (parsed.relationshipStatus) {
          blocks.push(
            { type: "heading", props: { level: 3 }, content: [{ type: "text" as const, text: "Relationship Status", styles: {} }] },
            { type: "paragraph", content: [{ type: "text" as const, text: parsed.relationshipStatus, styles: {} }] },
          );
        }
        if (parsed.recentContext) {
          blocks.push(
            { type: "heading", props: { level: 3 }, content: [{ type: "text" as const, text: "Recent Context", styles: {} }] },
            { type: "paragraph", content: [{ type: "text" as const, text: parsed.recentContext, styles: {} }] },
          );
        }
        if (parsed.keyTakeaways) {
          blocks.push(
            { type: "heading", props: { level: 3 }, content: [{ type: "text" as const, text: "Key Takeaways", styles: {} }] },
            { type: "paragraph", content: [{ type: "text" as const, text: parsed.keyTakeaways, styles: {} }] },
          );
        }

        return blocks;
      }

      return null;
    } catch (e) {
      return null;
    }
  }, [backendDeal]);

  // Transform backend engagements to list format
  const meetings = useMemo(() => {
    const transformed = rawMeetings.map((m: any) => {
      const title = m.displayName || m.description || 'Meeting';
      const attendees = m.contacts?.map((c: any) => {
        return getDisplayName(c.properties?.firstName, c.properties?.lastName, c.primaryEmail);
      }).filter(Boolean) || [];

      return {
        ...m,
        id: m.id,
        title,
        date: m.startTime
          ? formatDate(m.startTime as string)
          : '',
        duration: '30 min',
        attendees,
      };
    });

    return transformed.sort((a, b) => {
      const dateA = new Date(a.startTime as string || a.date || 0).getTime();
      const dateB = new Date(b.startTime as string || b.date || 0).getTime();
      return dateB - dateA;
    });
  }, [rawMeetings]);

  const emailEngagements = useMemo(() => {
    const transformed = rawEmailEngagements.map((e: any) => {
      const subject = e.title || e.displayName || e.description || 'Email';
      // Use metadataFrom (the actual email "From" header) for the sender,
      // matching the reference implementation (coffee-ui).
      const from = getSenderFromMetadata(e.metadataFrom);

      return {
        ...e,
        id: e.id,
        subject,
        displayName: subject,
        date: e.startTime
          ? formatDate(e.startTime as string)
          : '',
        from,
        senders: [from],
        direction: 'inbound' as const,
      };
    });

    return transformed.sort((a, b) => {
      const dateA = new Date(a.startTime as string || a.date || 0).getTime();
      const dateB = new Date(b.startTime as string || b.date || 0).getTime();
      return dateB - dateA;
    });
  }, [rawEmailEngagements]);

  // Subscribe to cross-component deal overrides (owners synced with the grid).
  useSyncExternalStore(
    dealEditStore.subscribe,
    () => (deal?.id ? dealEditStore.get(deal.id) : undefined),
    () => (deal?.id ? dealEditStore.get(deal.id) : undefined),
  );
  const storedOverride = deal?.id ? dealEditStore.get(deal.id) : undefined;
  const storedOwners = storedOverride?.owners;
  const storedContacts = storedOverride?.contacts;
  const storedOwnersOverride = storedOwners
    ? { owner: storedOwners[0], additionalOwners: storedOwners.slice(1) }
    : {};
  const storedContactsOverride = storedContacts
    ? { contacts: storedContacts.join(", ") }
    : {};
  const currentDeal = {
    ...deal,
    ...(useBackendData ? backendDealTransformed : {}),
    ...storedOwnersOverride,
    ...storedContactsOverride,
    ...overrides,
  };

  // "More" section: every remaining standard + custom field for the deal.
  // methodologyScope: a methodology column hidden from the grid must not
  // resurface in "More fields".
  const { customFields: dealCustomFieldsForMore } = useDealCustomFields({ methodologyScope: true });
  // User-added custom columns from the deals table Manage Columns UI live in
  // localStorage (per-workspace) as part of the `deals_v4` persisted state.
  // Surface them here so fields like "Lead Source 1" appear in More.
  // Mirror the column layout the user actually sees on the Deals grid.
  // NOTE: the previous read used the bare "deals_v6" key, which nothing ever
  // writes — useTableColumns persists under `table_cols_deals_v6*` — so the
  // drill-down never picked up grid order or user-added custom columns.
  const dealsTableState = useActiveGridColumnPrefs("deals_v6");
  const userCustomColumns = dealsTableState?.customColumns ?? [];
  // Default drill-down field order + hidden set mirror DEAL_GRID_COLUMNS and its
  // `hiddenByDefault` flags (see @/lib/grid/grid-column-config) so a fresh
  // drill-down matches the grid layout.
  const DEFAULT_DEAL_COLUMN_ORDER = useMemo<string[]>(
    () => [
      "name", "company", "description", "dealScore", "amount", "stage", "closeDate", "owner",
      "contacts", "lastEmail", "lastMeeting", "nextMeeting", "lastStep", "nextStep",
      "age", "timeInStage", "createdDate", "forecastCategory", "billing", "products",
    ],
    [],
  );
  const DEFAULT_HIDDEN_DEAL_COLUMNS = useMemo<string[]>(
    () => ["description", "dealScore", "age", "timeInStage", "createdDate", "forecastCategory", "billing", "products", "lastStep", "nextStep"],
    [],
  );
  const hiddenColKeys = useMemo(
    () => new Set<string>(dealsTableState?.hiddenKeys ?? DEFAULT_HIDDEN_DEAL_COLUMNS),
    [dealsTableState?.hiddenKeys, DEFAULT_HIDDEN_DEAL_COLUMNS],
  );
  const columnOrder = useMemo(
    () => (dealsTableState?.columnOrder && dealsTableState.columnOrder.length > 0
      ? dealsTableState.columnOrder
      : DEFAULT_DEAL_COLUMN_ORDER),
    [dealsTableState?.columnOrder, DEFAULT_DEAL_COLUMN_ORDER],
  );
  const [moreOpen, setMoreOpen] = useState(false);
  // Last/Next Step edits are held locally so the sheet shows the new text right
  // away (backendDeal isn't refetched after a save).
  const [stepEdits, setStepEdits] = useState<Record<string, string>>({});
  useEffect(() => { setStepEdits({}); }, [currentDeal?.id]);
  // Grid mounts persist via onFieldUpdate → updateCell. The global overlay and the
  // company sheet pass no handler, and every edit there used to update the sheet's
  // local overrides and reach nothing — so save directly when nobody upstream will.
  const persistField = (key: string, value: string) => {
    if (onFieldUpdate) onFieldUpdate(key, value);
    else if (currentDeal?.id && !previewMode) scheduleDealFieldUpdate(currentDeal.id, key, value);
  };
  const saveStep = (key: string, value: string) => {
    setStepEdits(prev => ({ ...prev, [key]: value }));
    persistField(key, value);
  };
  // Row objects carry '-' for an empty step (the grid's empty-cell sentinel);
  // these rows are editable, so don't offer "-" as the current value.
  const stepValue = useCallback((key: "lastStep" | "nextStep", backendVal: unknown): string | undefined => {
    if (stepEdits[key] !== undefined) return stepEdits[key];
    if (backendVal != null) return String(backendVal);
    const rowVal = (deal as any)?.[key];
    return rowVal === "-" ? "" : rowVal;
  }, [stepEdits, deal]);
  const moreFields = useMemo<MorePanelField[]>(() => {
    const props = (backendDeal?.properties ?? {}) as Record<string, any>;
    const custom = (backendDeal?.customProperties ?? {}) as Record<string, any>;
    const std: MorePanelField[] = [
      // Row objects carry '-' for an empty description (the grid's empty-cell
      // sentinel); this row is editable, so strip it rather than offering "-" as
      // the current value and letting a save write it back.
      {
        key: "description",
        label: t("fields.deals.description"),
        type: "text",
        value: props.description ?? ((deal as any)?.description === "-" ? "" : (deal as any)?.description),
      },
      { key: "dealScore", label: t("fields.deals.dealScore"), type: "number", readOnly: true, value: props.dealScore ?? (deal as any)?.dealScore },
      { key: "createdDate", label: t("fields.deals.createdDate"), type: "date", readOnly: true, value: (backendDeal as any)?.createTs ? formatDate((backendDeal as any).createTs) : (deal as any)?.createdDate },
      { key: "lastStep", label: t("fields.deals.lastStep"), type: "text", value: stepValue("lastStep", props.lastActivityText) },
      { key: "nextStep", label: t("fields.deals.nextStep"), type: "text", value: stepValue("nextStep", props.nextActivityText) },
      { key: "age", label: t("fields.deals.age"), type: "text", readOnly: true, value: (deal as any)?.age != null ? String((deal as any).age) : undefined },
      { key: "timeInStage", label: t("fields.deals.timeInStage"), type: "text", readOnly: true, value: (deal as any)?.timeInStage ?? ((backendDeal as any)?.inStageSince ? formatDate((backendDeal as any).inStageSince) : undefined) },
      { key: "forecastCategory", label: t("fields.deals.forecastCategory"), type: "single-select", options: [...FORECAST_CATEGORIES], value: props.forecastCategory ?? (deal as any)?.forecastCategory },
      { key: "billing", label: t("fields.deals.billing"), type: "single-select", options: ["Monthly", "Quarterly", "Annually", "One-time", "Usage-based"], value: (currentDeal as any)?.billing },
      { key: "products", label: t("fields.deals.products"), type: "text", readOnly: true, value: (() => {
        const lines = (currentDeal as any)?._lineItems || (currentDeal as any)?.lineItems;
        if (Array.isArray(lines) && lines.length > 0) return t("detailSheets.itemCount", { count: lines.length });
        return undefined;
      })() },
    ];
    // Merge every known custom field definition (from useDealCustomFields) with
    // orphan definitions persisted in the grid's `customColumns` localStorage.
    // This mirrors the People/Companies drill-down behavior so hidden custom
    // fields always appear in "More fields", even if the backend registry is
    // out of sync.
    const knownKeys = new Set<string>();
    const allCustom: Array<{ key: string; label: string; type: string; selectOptions?: string[]; crmName?: string }> = [];
    for (const cf of dealCustomFieldsForMore) {
      const key = `custom_${cf.crmName}`;
      knownKeys.add(key);
      allCustom.push({ key, label: cf.label, type: cf.type, selectOptions: cf.selectOptions, crmName: cf.crmName });
    }
    for (const cc of userCustomColumns) {
      if (!cc.key.startsWith("custom_") || knownKeys.has(cc.key)) continue;
      knownKeys.add(cc.key);
      allCustom.push({ key: cc.key, label: cc.label, type: (cc as any).type || "text", selectOptions: (cc as any).selectOptions });
    }
    const customRows: MorePanelField[] = allCustom
      .filter(cf => hiddenColKeys.has(cf.key) || (cf.crmName ? hiddenColKeys.has(cf.crmName) : false))
      .map(cf => {
        const key = cf.key;
        const backendVal = cf.crmName ? custom[cf.crmName] : undefined;
        // Prefer edit-store value so grid <-> detail edits stay in sync.
        const storeVal = (storedOverride as any)?.[key];
        const overrideVal = (overrides as any)?.[key];
        const value =
          storeVal !== undefined
            ? storeVal
            : overrideVal !== undefined
              ? overrideVal
              : (backendVal ?? (currentDeal as any)?.[key] ?? "");
        return {
          key,
          label: cf.label,
          type: cf.type,
          options: cf.selectOptions,
          value: value as any,
          isCustom: true,
        };
      });
    // Non-custom user-added columns (rare) — kept as read-only rows.
    const seenUser = new Set([...std.map(f => f.key), ...customRows.map(f => f.key)]);
    const userRows: MorePanelField[] = userCustomColumns
      .filter(c => !c.key.startsWith("custom_") && !seenUser.has(c.key))
      .map(c => ({
        key: c.key,
        label: c.label,
        type: (c as any).type ?? "text",
        options: (c as any).selectOptions,
        value: (currentDeal as any)?.[c.key],
        isCustom: true,
        readOnly: true,
      }));
    const stdHidden = std.filter(f => hiddenColKeys.has(f.key));
    return [...stdHidden, ...customRows, ...userRows];
  }, [backendDeal, dealCustomFieldsForMore, deal, currentDeal, userCustomColumns, hiddenColKeys, storedOverride, overrides, stepValue]);

  // People from backend deal contacts
  const backendDealPeople = useMemo(() => {
    if (!useBackendData || !backendDeal?.contacts?.edges) return [];
    return backendDeal.contacts.edges.map((edge) => {
      const c = edge.node;
      const name = getDisplayName(c.properties?.firstName, c.properties?.lastName, c.primaryEmail);
      return {
        id: c.id,
        name,
        title: c.properties?.jobTitle || '',
        company: currentDeal.company || '',
        email: c.primaryEmail || '',
        phone: '',
      };
    });
  }, [useBackendData, backendDeal, currentDeal.company]);

  const hasDeal = !!deal;

  const favorited = deal ? isFavorite(deal.id, "deal") : false;
  const handleToggleFavorite = () => {
    if (!deal) return;
    toggleFavorite({
      id: deal.id,
      type: "deal",
      name: currentDeal.name,
      subtitle: `${currentDeal.company || ""} · ${formatAmount(currentDeal.amount || 0)}`,
    });
  };

  // Reset per-deal local state when deal changes — the sheet instance is
  // reused across switches (no key remount), so unsaved edits and the
  // overrides merge would otherwise carry the previous deal's values onto
  // the newly selected one.
  useEffect(() => {
    setLocalPipelineId(null);
    setOverrides({});
    setEditingFieldRaw(null);
  }, [deal?.id]);

  // Drop stale local overrides when the server record changes underneath us
  // (e.g. a HubSpot re-sync reverts a field the user edited here). `overrides`
  // is spread last in `currentDeal`, so without this it masks the fresh value
  // indefinitely — the source of the "two users see different close dates" bug.
  // asOfTs is the deal's server-side "last updated" stamp, so comparing it is a
  // server-vs-server monotonic check (no client clock skew). Guard on a defined→
  // different-defined transition: skip the initial load so an edit made before
  // the first fetch survives, and note asOfTs can't advance to a reverted value
  // until a backend write lands — so this never fires inside the debounced-write
  // window and can't clobber an in-flight edit.
  const prevAsOfTsRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const curr = backendDeal?.asOfTs ?? undefined;
    const prev = prevAsOfTsRef.current;
    prevAsOfTsRef.current = curr;
    if (prev && curr && prev !== curr) {
      setOverrides({});
    }
  }, [backendDeal?.asOfTs]);

  // Get pipeline stages with colors — local override > prop > the deal's stored stage
  // key > stage-name match > first. The stored key matters wherever no pipelineId is
  // passed (global overlay, company sheet): names repeat across pipelines, and a save
  // sends the resolved pipeline's stage code.
  const effectivePipelineId = localPipelineId || pipelineId;
  const storedStage = backendDeal?.properties?.stage;
  const pipeline = useMemo(
    () => resolveDealPipeline(pipelines, { pipelineId: effectivePipelineId, storedStage, stageName: currentDeal.stage }),
    [pipelines, effectivePipelineId, storedStage, currentDeal.stage],
  );
  const hasMultiplePipelines = pipelines.length > 1;
  const stages = pipeline?.stages || [];
  const currentStage = stages.find(s => s.name === currentDeal.stage);
  const stageColor = currentStage?.color || "#94a3b8";
  const stagePct = currentStage?.pct ?? 0;
  // Saving directly needs the deal's stored stage to know its pipeline; until getDeal
  // lands, the picker would offer the first pipeline's stages and save one of those.
  // Until getPipelines lands the stages are the dummy defaults (no externalId), so a
  // pick would send a display name the backend may resolve in another pipeline.
  const stageEditable = !isReadOnly && (previewMode || (hasBackendPipelines && (!!onStageChange || !!onFieldUpdate || useBackendData)));

  const contacts = (currentDeal.contacts || "").split(",").map(c => c.trim()).filter(Boolean);
  const dummyDealPeople = allPeople.filter(p => contacts.includes(p.name) || p.company === currentDeal.company);
  const dealPeopleRaw = useBackendData ? backendDealPeople : dummyDealPeople;

  // Dedupe contacts so a person with multiple emails appears once.
  const dealPeople = (() => {
    const byKey = new Map<string, typeof dealPeopleRaw[number]>();
    for (const p of dealPeopleRaw) {
      const nameKey = (p.name || "").trim().toLowerCase();
      const key = nameKey || p.id;
      if (!byKey.has(key)) byKey.set(key, p);
    }
    return Array.from(byKey.values());
  })();

  const startEditName = () => {
    setEditName(currentDeal.name || "");
    setEditingField("name");
  };
  const saveName = () => {
    const trimmed = editName.trim();
    if (trimmed) {
      setOverrides(prev => ({ ...prev, name: trimmed }));
      persistField("name", trimmed);
    }
    setEditingField(null);
  };

  const startEditAmount = () => {
    setEditAmount(String(currentDeal.amount || 0));
    setEditingField("amount");
  };
  const saveAmount = () => {
    const num = parseInt(editAmount.replace(/[^0-9]/g, ""), 10);
    if (!isNaN(num)) {
      setOverrides(prev => ({ ...prev, amount: num }));
      persistField("amount", editAmount);
    }
    setEditingField(null);
  };

  const startEditStage = () => {
    setEditStage(currentDeal.stage || "");
    setEditingField("stage");
  };
  const saveStage = (stageName: string, stageExternalId?: string) => {
    // Revert restores the prior override, or drops the key when there wasn't one —
    // setting `stage: undefined` would mask the backend value with a blank chip.
    const hadOverride = "stage" in overrides;
    const prevOverride = overrides.stage;
    const revert = () => setOverrides(prev => {
      const next = { ...prev };
      if (hadOverride) next.stage = prevOverride;
      else delete next.stage;
      return next;
    });
    setOverrides(prev => ({ ...prev, stage: stageName }));
    setEditingField(null);
    // Resolve externalId from the visible stages (the deal's own pipeline) when the
    // caller didn't supply one. The pipeline-change path passes it explicitly because
    // the target stage belongs to a different pipeline than the one shown here.
    const externalId = stageExternalId ?? stages.find(s => s.name === stageName)?.externalId;
    if (onStageChange) {
      // Revert the optimistic override if the backend sync-guard rejected the move.
      Promise.resolve(onStageChange(stageName, externalId || stageName)).then(ok => {
        if (ok === false) revert();
      });
    } else if (onFieldUpdate) {
      onFieldUpdate("stage", stageName);
    } else if (currentDeal.id && !previewMode) {
      // No handler (global overlay, company sheet): save it here. The stage guard
      // rejects with a readable reason ("fill in required field(s) first: …"), so
      // surface that and roll the chip back rather than the generic failure toast.
      updateDeal({
        id: currentDeal.id,
        properties: { stage: externalId || stageName },
        suppressErrorToast: true,
      }).then(res => {
        if (res.success) return;
        revert();
        const error = (res as { error?: unknown }).error;
        const gqlMessage = (error as { graphQLErrors?: { message?: string }[] } | undefined)
          ?.graphQLErrors?.[0]?.message;
        const message = gqlMessage || (error instanceof Error ? error.message : "");
        toast.error(message || t("deals.cantMoveToStage", { stage: stageName }));
      });
    }
  };

  const startEditCloseDate = () => {
    setEditingField("closeDate");
  };
  const saveCloseDate = (date: Date | undefined) => {
    if (date) {
      const formatted = format(date, "MMM d, yyyy");
      setOverrides(prev => ({ ...prev, closeDate: formatted }));
      persistField("closeDate", formatted);
    }
    setEditingField(null);
  };

  const startEditOwner = () => {
    setEditOwner(currentDeal.owner || "");
    setEditingField("owner");
  };
  const saveOwner = (val: string) => {
    setOverrides(prev => ({ ...prev, owner: val }));
    onFieldUpdate?.("owner", val);
    setEditingField(null);
  };

  // Multi-owner helpers. The primary owner is `currentDeal.owner`; any
  // additional owners live on `additionalOwners`. All mutations go through
  // setOverrides so they persist for the life of the sheet like other edits.
  const allOwners: string[] = [
    ...(currentDeal.owner ? [currentDeal.owner] : []),
    ...((currentDeal.additionalOwners || []).filter(Boolean)),
  ];
  const setOwners = (next: string[]) => {
    const primary = next[0];
    const rest = next.slice(1);
    setOverrides(prev => ({ ...prev, owner: primary, additionalOwners: rest }));
    if (deal?.id) dealEditStore.merge(deal.id, { owners: next });
    // Persist primary + additional owners together (parent maps names → ids).
    if (onOwnersUpdate) {
      onOwnersUpdate(next);
      return;
    }
    // No parent handler: map names → member ids here, as DealsPage's persistDealOwners does.
    if (!deal?.id || previewMode) return;
    const primaryMember = realWorkspaceMembers.find(m => m.name === primary);
    updateDeal({
      id: deal.id,
      properties: {
        ...(primaryMember?.id ? { ownerId: primaryMember.id } : {}),
        additionalOwnerIds: mapOwnerNamesToIds(rest, realWorkspaceMembers),
      },
    });
  };
  const addOwner = (name: string) => {
    if (!name || allOwners.includes(name)) return;
    setOwners([...allOwners, name]);
  };
  const removeOwner = (idx: number) => {
    setOwners(allOwners.filter((_, i) => i !== idx));
  };
  const makePrimaryOwner = (idx: number) => {
    if (idx === 0) return;
    const item = allOwners[idx];
    setOwners([item, ...allOwners.filter((_, i) => i !== idx)]);
  };

  // Persisted wire values, never translated.
  const billingOptions = useMemo(() => ["Monthly", "Quarterly", "Annually", "One-time", "Usage-based"], []);

  /**
   * Hoisted out of the JSX below so the `key` and `type` strings read as the
   * identifiers they are: inside a JSX expression container the i18n lint
   * treats every literal as user-facing copy. Only `label` is copy, and it
   * comes from the catalog.
   */
  const allMoreForVisible = useMemo<MorePanelField[]>(() => [
    { key: "dealScore", label: t("fields.deals.dealScore"), type: "number", readOnly: true, value: (backendDeal?.properties as any)?.dealScore ?? (deal as any)?.dealScore, icon: Gauge },
    { key: "age", label: t("fields.deals.age"), type: "text", readOnly: true, value: (deal as any)?.age != null ? String((deal as any).age) : undefined, icon: Clock },
    { key: "timeInStage", label: t("fields.deals.timeInStage"), type: "text", readOnly: true, value: (deal as any)?.timeInStage ?? ((backendDeal as any)?.inStageSince ? formatDate((backendDeal as any).inStageSince) : undefined), icon: Clock },
    { key: "createdDate", label: t("fields.deals.createdDate"), type: "date", readOnly: true, value: (backendDeal as any)?.createTs ? formatDate((backendDeal as any).createTs) : (deal as any)?.createdDate, icon: Calendar },
    { key: "lastStep", label: t("fields.deals.lastStep"), type: "text", value: stepValue("lastStep", (backendDeal?.properties as any)?.lastActivityText), icon: Activity },
    { key: "nextStep", label: t("fields.deals.nextStep"), type: "text", value: stepValue("nextStep", (backendDeal?.properties as any)?.nextActivityText), icon: Activity },
    { key: "forecastCategory", label: t("fields.deals.forecastCategory"), type: "single-select", options: [...FORECAST_CATEGORIES], value: (backendDeal?.properties as any)?.forecastCategory ?? (deal as any)?.forecastCategory, icon: Layers },
    { key: "billing", label: t("fields.deals.billing"), type: "single-select", options: billingOptions, value: (currentDeal as any)?.billing, icon: DollarSign },
    { key: "products", label: t("fields.deals.products"), type: "text", readOnly: true, value: (() => {
      const lines = (currentDeal as any)?._lineItems || (currentDeal as any)?.lineItems;
      if (Array.isArray(lines) && lines.length > 0) return t("detailSheets.itemCount", { count: lines.length });
      return undefined;
    })(), icon: Package },
  ], [backendDeal, deal, currentDeal, billingOptions, t, formatDate, stepValue]);

  const startEditBilling = () => {
    setEditBilling(currentDeal.billing || "");
    setEditingField("billing");
  };
  const saveBilling = (val: string) => {
    setOverrides(prev => ({ ...prev, billing: val }));
    setEditingField(null);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) setNavStack([]);
    onOpenChange(open);
  };

  const pushNav = (item: NavigationItem) => setNavStack(prev => [...prev, item]);
  const popNav = () => setNavStack(prev => prev.slice(0, -1));

  const currentNav = navStack.length > 0 ? navStack[navStack.length - 1] : null;

  const handleCompanyClick = async () => {
    if (!currentDeal.company) return;
    if (previewMode) {
      const company = allCompanies.find(c => c.name === currentDeal.company);
      if (company) {
        pushNav({ type: "company", data: { id: company.id, name: company.name, industry: company.industry, description: company.description, location: company.location, website: company.website, employees: company.employees, revenue: company.revenue } });
      }
      return;
    }
    // Production: prefer the real account id the deal already carries; the
    // nested CompanyDetailSheet fetches the rest. Fall back to resolving the
    // name over the backend when the deal has no linked account id.
    if (currentDeal.companyId) {
      pushNav({ type: "company", data: { id: currentDeal.companyId, name: currentDeal.company } });
      return;
    }
    const resolved = await resolveCompany(currentDeal.company);
    if (resolved) {
      pushNav({ type: "company", data: { id: resolved.id, name: resolved.name, website: resolved.website } });
    }
  };

  // Handle nav types that open separate sheets (must not setState during render)
  useEffect(() => {
    if (!currentNav) return;
    if (currentNav.type === "person") {
      setSelectedPerson(currentNav.data);
      setPersonSheetOpen(true);
      popNav();
    } else if (currentNav.type === "company") {
      setSelectedCompany(currentNav.data);
      setCompanySheetOpen(true);
      popNav();
    } else if (currentNav.type === "meeting") {
      const meetingDate = new Date(currentNav.data.date || currentNav.data.startTime);
      const isUpcoming = meetingDate > new Date();
      if (isUpcoming) {
        setBriefingMeeting({
          id: currentNav.data.id,
          title: currentNav.data.title || currentNav.data.name || 'Meeting',
          date: formatDate(meetingDate),
          time: formatTime(meetingDate),
          duration: currentNav.data.duration || '30 min',
          type: 'Meeting',
          attendees: currentNav.data.attendees || [],
          link: undefined,
        });
        setBriefingOpen(true);
      } else {
        setMeetingIntelligenceNote({
          id: currentNav.data.id,
          name: currentNav.data.title || currentNav.data.name || 'Meeting',
          date: currentNav.data.date || currentNav.data.startTime,
          people: currentNav.data.attendees || [],
          company: deal?.company || '',
        });
        setMeetingIntelligenceTab("summary");
        setMeetingIntelligenceOpen(true);
      }
      popNav();
    } else if (currentNav.type === "briefing") {
      const meetingData = currentNav.data;
      setBriefingMeeting({
        id: meetingData.id,
        title: meetingData.title || meetingData.name || 'Meeting',
        date: meetingData.date,
        time: meetingData.time || '',
        duration: meetingData.duration || '30 min',
        type: 'Meeting',
        attendees: meetingData.attendees || [],
        link: undefined,
      });
      setBriefingOpen(true);
      popNav();
    }
  }, [currentNav]);

  if (!hasDeal) return null;

  return (
    <>
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent expandable hideOverlay={hideOverlay} className="sm:max-w-[50vw] w-full overflow-y-auto p-0">
        {currentNav ? (
          <div className="px-6 pt-6 pb-6">
            {currentNav.type === "email" && <EmailDrillDown email={currentNav.data} onBack={popNav} />}
            {currentNav.type === "file" && <FileDrillDown file={currentNav.data} onBack={popNav} />}
            {currentNav.type === "deal" && <DealDrillDown deal={currentNav.data} onBack={popNav} onNavigate={pushNav} />}
            {currentNav.type === "note" && <NoteDrillDown note={currentNav.data} onBack={popNav} />}
          </div>
        ) : (
          <>
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-border">
          {onBack && (
            <button onClick={onBack} className="flex items-center gap-1 text-muted-foreground text-xs hover:text-foreground mb-3 transition">
              <ArrowLeft className="h-4 w-4" /> {t("detailSheets.back")}
            </button>
          )}
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
              {currentDeal.company ? <CompanyLogo name={currentDeal.company} domain={allCompanies.find(c => c.name === currentDeal.company)?.website} /> : <DollarSign className="h-5 w-5 text-muted-foreground" />}
            </div>
            <div className="flex-1 min-w-0">
              <SheetHeader className="p-0 space-y-0">
                <div className="flex items-center gap-2">
                  {editingField === "name" ? (
                    <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                      <SheetTitle className="sr-only">{currentDeal.name}</SheetTitle>
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveName();
                          if (e.key === "Escape") setEditingField(null);
                        }}
                        className="h-8 text-base font-semibold"
                        autoFocus
                      />
                      <button onClick={saveName} className="text-accent hover:opacity-80"><Check className="h-3.5 w-3.5" /></button>
                      <button onClick={() => setEditingField(null)} className="text-muted-foreground hover:opacity-80"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 min-w-0">
                      <SheetTitle
                        className={cn("text-lg font-semibold text-left truncate", !isReadOnly && "cursor-text")}
                        onDoubleClick={isReadOnly ? undefined : startEditName}
                      >
                        {currentDeal.name}
                      </SheetTitle>
                      {!isReadOnly && (
                        <button onClick={startEditName} className="text-muted-foreground hover:text-foreground transition shrink-0">
                          <Pencil className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  )}
                  <button onClick={handleToggleFavorite} className="shrink-0 hover:scale-110 transition">
                    <Star className={cn("h-3.5 w-3.5", favorited ? "fill-accent text-accent" : "text-muted-foreground")} />
                  </button>
                  {drillDownActionsEnabled && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="shrink-0 hover:bg-accent rounded p-1 transition">
                          <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => setAddToListOpen(true)}>
                          <ListPlus className="h-4 w-4 mr-2" /> {t("common.addToList")}
                        </DropdownMenuItem>
                        {onClone && (
                          <DropdownMenuItem onClick={() => onClone(currentDeal as any)}>
                            <Copy className="h-4 w-4 mr-2" /> {t("detailSheets.clone")}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => setConfirmDeleteOpen(true)} className="text-destructive focus:text-destructive">
                          <Trash2 className="h-4 w-4 mr-2" /> {t("common.delete")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </SheetHeader>
            </div>
          </div>

          {/* Field grid — order + visibility synced with the deals grid.
              Standard fields render via the `primary` map below; other visible
              standard columns and custom fields fall through to MoreFieldRow. */}
          <div className="mt-4 space-y-0.5">
            {(() => {
              const primary: Record<string, JSX.Element> = {
                company: (
                  <FieldRow icon={Building2} label={t("detailSheets.account")}>
                    {currentDeal.company ? (
                      <EditableDetailField
                        hideIcon
                        value={currentDeal.company}
                        onChange={v => setOverrides(prev => ({ ...prev, company: v }))}
                        icon={null as any}
                        suggestions={[...new Set(allCompanies.map(c => c.name))]}
                        onDrillDown={handleCompanyClick}
                        className="text-xs text-accent"
                      />
                    ) : <span className="text-muted-foreground/60">—</span>}
                  </FieldRow>
                ),
                amount: (
                  <FieldRow icon={DollarSign} label={t("fields.deals.amount")}>
                    {editingField === "amount" ? (
                      <div className="flex items-center gap-1.5 min-w-0" onClick={e => e.stopPropagation()}>
                        <Input
                          value={editAmount}
                          autoFocus
                          onChange={e => setEditAmount(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter") saveAmount(); if (e.key === "Escape") setEditingField(null); }}
                          className="h-6 text-xs w-28 px-1.5"
                        />
                        <button onClick={saveAmount} className="text-accent"><Check className="h-3 w-3" /></button>
                        <button onClick={() => setEditingField(null)} className="text-muted-foreground"><X className="h-3 w-3" /></button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={isReadOnly ? undefined : startEditAmount}
                        className={cn("text-xs text-foreground text-left truncate", !isReadOnly && "hover:text-accent")}
                      >
                        {formatAmount(currentDeal.amount || 0)}
                      </button>
                    )}
                  </FieldRow>
                ),
                stage: (
                  <FieldRow icon={TrendingUp} label={t("fields.deals.stage")}>
                    {!stageEditable ? (
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: stageColor }} />
                        <span>{currentDeal.stage || "—"}</span>
                        <span className="text-muted-foreground">{stagePct}%</span>
                      </span>
                    ) : (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="inline-flex items-center gap-1.5 text-xs hover:bg-muted/30 rounded px-1 -mx-1 py-0.5">
                            <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: stageColor }} />
                            <span>{stageLabel(stages.find(s => s.name === currentDeal.stage)) || currentDeal.stage || "—"}</span>
                            <span className="text-muted-foreground">{stagePct}%</span>
                            <ChevronDown className="h-3 w-3 text-muted-foreground" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="p-1 z-[60] w-56" align="start">
                          {stages.map(s => (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => saveStage(s.name, s.externalId)}
                              className="flex w-full items-center gap-2 rounded px-2 py-1 text-xs hover:bg-accent hover:text-accent-foreground"
                            >
                              <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                              <span className="flex-1 text-left">{stageLabel(s)}</span>
                              <span className="text-muted-foreground">{s.pct}%</span>
                            </button>
                          ))}
                        </PopoverContent>
                      </Popover>
                    )}
                  </FieldRow>
                ),
                closeDate: (
                  <FieldRow icon={Calendar} label={t("fields.deals.closeDate")}>
                    {isReadOnly ? (
                      <span className="text-xs">{currentDeal.closeDate || <span className="text-muted-foreground/60">—</span>}</span>
                    ) : (
                      <Popover open={editingField === "closeDate"} onOpenChange={o => { if (!o) setEditingField(null); }}>
                        <PopoverTrigger asChild>
                          <button className="text-xs text-left hover:bg-muted/30 rounded px-1 -mx-1 py-0.5" onClick={startEditCloseDate}>
                            {currentDeal.closeDate || <span className="text-muted-foreground/60">{t("detailSheets.pickADate")}</span>}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 z-[60]" align="start">
                          <CalendarPicker mode="single" selected={editCloseDate} onSelect={saveCloseDate} initialFocus />
                        </PopoverContent>
                      </Popover>
                    )}
                  </FieldRow>
                ),
                owner: (
                  <FieldRow icon={Users} label={allOwners.length > 1 ? t("detailSheets.owners") : t("fields.deals.owner")}>
                    <Popover open={editingField === "owner"} onOpenChange={o => { if (o) { if (!isReadOnly) setEditingField("owner"); } else setEditingField(null); }}>
                      <div className="flex items-center gap-1.5 min-w-0 group/owner w-full">
                        {currentDeal.owner && <UserAvatar name={currentDeal.owner} size="xs" />}
                        <span className="text-xs truncate">{currentDeal.owner || <span className="text-muted-foreground/60">—</span>}</span>
                        {allOwners.length > 1 && (
                          <button
                            type="button"
                            className="shrink-0 text-[10px] font-medium bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 leading-none hover:bg-muted/70"
                            onClick={e => { e.stopPropagation(); if (!isReadOnly) setEditingField("owner"); }}
                          >
                            +{allOwners.length - 1}
                          </button>
                        )}
                        {!isReadOnly && (
                          <PopoverTrigger asChild>
                            <button className="shrink-0 text-muted-foreground opacity-0 group-hover/owner:opacity-100 hover:text-foreground" onClick={e => e.stopPropagation()}>
                              <Pencil className="h-3 w-3" />
                            </button>
                          </PopoverTrigger>
                        )}
                      </div>
                      <PopoverContent align="start" className="w-72 p-3 z-[70]" onClick={e => e.stopPropagation()}>
                        <div className="space-y-1.5 mb-3">
                          {allOwners.length === 0 && <p className="text-xs text-muted-foreground">{t("detailSheets.noOwnersYet")}</p>}
                          {allOwners.map((name, idx) => (
                            <div key={`${name}-${idx}`} className="flex items-center justify-between gap-2 group/item">
                              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                <UserAvatar name={name} size="xs" />
                                <span className={cn("text-sm truncate", idx === 0 && "font-medium")}>{name}</span>
                                {idx === 0 && <span className="shrink-0 text-[10px] font-medium bg-accent/10 text-accent rounded px-1.5 py-0.5 leading-none">{t("detailSheets.primary")}</span>}
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                {idx !== 0 && (
                                  <button onClick={() => makePrimaryOwner(idx)} className="opacity-0 group-hover/item:opacity-100 transition text-muted-foreground hover:text-accent" title={t("detailSheets.makePrimary")}>
                                    <Star className="h-3 w-3" />
                                  </button>
                                )}
                                <button onClick={() => removeOwner(idx)} className="opacity-0 group-hover/item:opacity-100 transition text-muted-foreground hover:text-destructive" title={t("detailSheets.remove")}>
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <Select value="" onValueChange={v => addOwner(v)}>
                          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder={t("detailSheets.addOwnerPlaceholder")} /></SelectTrigger>
                          <SelectContent className="z-[80]">
                            {teamMembers.filter(m => !allOwners.includes(m)).map(m => (
                              <SelectItem key={m} value={m}>
                                <div className="flex items-center gap-2"><UserAvatar name={m} size="xs" />{m}</div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </PopoverContent>
                    </Popover>
                  </FieldRow>
                ),
                lastEmail: (
                  <FieldRow icon={Mail} label={t("fields.deals.lastEmail")}>
                    {currentDeal.lastEmail && currentDeal.lastEmail !== "-" ? (
                      <button
                        onClick={() => {
                          if (useBackendData && backendDeal?.lastEmail) {
                            const emailDate = backendDeal.lastEmail.startTime ? formatDate(backendDeal.lastEmail.startTime) : '';
                            pushNav({ type: "email", data: { id: backendDeal.lastEmail.id, subject: backendDeal.lastEmail.displayName || 'Email', date: emailDate, from: currentDeal.company || '', direction: 'inbound' as const } });
                          } else {
                            pushNav({ type: "email", data: dummyEmails[0] });
                          }
                        }}
                        className="text-xs text-foreground hover:text-accent hover:underline truncate text-left"
                      >{currentDeal.lastEmail}</button>
                    ) : <span className="text-muted-foreground/60">—</span>}
                  </FieldRow>
                ),
                lastMeeting: (
                  <FieldRow icon={Video} label={t("fields.deals.lastMeeting")}>
                    {currentDeal.lastMeeting && currentDeal.lastMeeting !== "-" ? (
                      lastMeetingPrivacy.canView ? (
                        <button
                          onClick={() => {
                            const lastEngagement = useBackendData ? backendDeal?.lastActivityEngagement : null;
                            const fallbackMeeting = useBackendData ? null : dummyMeetings[0];
                            setMeetingIntelligenceNote({
                              id: lastEngagement?.id || fallbackMeeting?.id || '1',
                              name: lastEngagement?.displayName || fallbackMeeting?.title || 'Meeting',
                              date: currentDeal.lastMeeting || '',
                              people: fallbackMeeting?.attendees || [],
                              company: currentDeal.company || '',
                            });
                            setMeetingIntelligenceTab("summary");
                            setMeetingIntelligenceOpen(true);
                          }}
                          className="text-xs text-foreground hover:text-accent hover:underline truncate text-left"
                        >{currentDeal.lastMeeting}</button>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground cursor-default">
                              {currentDeal.lastMeeting}<Lock className="h-3 w-3" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top">{t("detailSheets.meetingParticipantsOnly")}</TooltipContent>
                        </Tooltip>
                      )
                    ) : <span className="text-muted-foreground/60">—</span>}
                  </FieldRow>
                ),
                nextMeeting: (
                  <FieldRow icon={Video} label={t("fields.deals.nextMeeting")}>
                    {currentDeal.nextMeeting && currentDeal.nextMeeting !== "-" ? (
                      nextMeetingPrivacy.canView ? (
                        <button
                          onClick={() => {
                            const nextMeeting = useBackendData && backendDeal?.nextStepEngagements?.[0] ? backendDeal.nextStepEngagements[0] : null;
                            setBriefingMeeting({
                              id: nextMeeting?.id || 'upcoming-deal',
                              title: nextMeeting?.displayName || 'Upcoming Meeting',
                              date: currentDeal.nextMeeting!,
                              time: '10:00 AM',
                              duration: '30 min',
                              type: 'Google Meet',
                              attendees: useBackendData ? (meetings[0]?.attendees || []) : (dummyMeetings[0]?.attendees || []),
                              link: undefined,
                            });
                            setBriefingOpen(true);
                          }}
                          className="text-xs text-accent font-medium hover:underline truncate text-left"
                        >{currentDeal.nextMeeting}</button>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground cursor-default">
                              {currentDeal.nextMeeting}<Lock className="h-3 w-3" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top">{t("detailSheets.meetingParticipantsOnly")}</TooltipContent>
                        </Tooltip>
                      )
                    ) : <span className="text-muted-foreground/60">—</span>}
                  </FieldRow>
                ),
                contacts: (
                  <FieldRow icon={Users} label={t("fields.deals.contacts")}>
                    {/*
                      Production and preview use different cells, mirroring the
                      Deals grid (DealsPage "contacts" column). MultiContactCell
                      is name-keyed: it resolves a name to a candidate and, when
                      that misses, synthesizes `{ id: name }`. Fed with dummy
                      people it always missed on real deals, so drilling into a
                      contact opened the person sheet with a *name* in the id
                      slot — getContact returned null and every tab in that
                      sheet came up empty. EditableContacts carries real ids and
                      searches people over the backend instead.
                    */}
                    {previewMode ? (
                      <MultiContactCell
                        contacts={contacts}
                        candidates={allPeople.map(p => ({
                          id: p.id, name: p.name, email: p.email, company: p.company,
                          title: (p as any).title, avatar: (p as any).avatar,
                        })) as ContactCandidate[]}
                        onChange={(next) => {
                          dealEditStore.merge(currentDeal.id, { contacts: next });
                          onFieldUpdate?.("contacts", next.join(", "));
                        }}
                        onDrillDown={(c) => {
                          const person = allPeople.find(p => p.id === c.id || p.name === c.name);
                          setSelectedPerson({ id: person?.id || c.id, name: c.name, email: c.email });
                          setPersonSheetOpen(true);
                        }}
                      />
                    ) : !useBackendData ? (
                      // getDeal still in flight: real contacts (and their ids)
                      // aren't loaded yet, so show the grid row's names read-only
                      // rather than flashing an empty field.
                      contacts.filter(c => c !== "-").length > 0
                        ? <span className="text-sm">{contacts.filter(c => c !== "-").join(", ")}</span>
                        : <span className="text-muted-foreground/60">—</span>
                    ) : (
                      <EditableContacts
                        value={backendDealPeople.map(p => ({ id: p.id, name: p.name, email: p.email }))}
                        onChange={async (selected) => {
                          // Persist ids, not names — same shape the grid sends.
                          const result = await updateDeal({
                            id: currentDeal.id,
                            properties: { contactIds: selected.map(c => c.id) },
                          });
                          if (!result.success) return;
                          dealEditStore.merge(currentDeal.id, { contacts: selected.map(c => c.name) });
                        }}
                        onDrillDown={(contact) => {
                          setSelectedPerson({ id: contact.id, name: contact.name, email: contact.email ?? undefined });
                          setPersonSheetOpen(true);
                        }}
                      />
                    )}
                  </FieldRow>
                ),
              };

              const seen = new Set<string>();
              const rows: JSX.Element[] = [];
              const emit = (key: string) => {
                if (seen.has(key) || key === "name" || hiddenColKeys.has(key)) return;
                seen.add(key);
                if (primary[key]) {
                  rows.push(<div key={key}>{primary[key]}</div>);
                  return;
                }
                const mf = allMoreForVisible.find(f => f.key === key);
                if (mf) rows.push(
                  <MoreFieldRow
                    key={key}
                    field={mf}
                    onChange={(k, v) => {
                      const str = Array.isArray(v) ? v.join(",") : String(v);
                      if (k === "lastStep" || k === "nextStep") saveStep(k, str);
                      else if (k === "forecastCategory") persistField(k, str);
                      else onFieldUpdate?.(k, str);
                    }}
                  />
                );
              };
              for (const k of columnOrder) emit(k);
              return rows;
            })()}
            <FieldRow icon={ListIcon} label={t("detailSheets.lists")}>
              <EntityListBadges type="deal" id={currentDeal.id} compact />
            </FieldRow>
            {moreFieldsEnabled && moreFields.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setMoreOpen(v => !v)}
                  aria-expanded={moreOpen}
                  className="mt-1 inline-flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground transition"
                >
                  {moreOpen ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                  <span>{moreOpen ? t("detailSheets.fewerFields") : t("detailSheets.moreFields")}</span>
                </button>
                <MoreFieldsPanel
                  open={moreOpen}
                  onClose={() => setMoreOpen(false)}
                  entity="deals"
                  fields={moreFields}
                  onChange={(k, v) => {
                    const strVal = Array.isArray(v) ? v.join("||") : String(v);
                    if (k === "lastStep" || k === "nextStep") { saveStep(k, strVal); return; }
                    // `description` and `forecastCategory` are standard properties, but
                    // they need the same direct-save fallback as custom fields: without
                    // `onFieldUpdate` (drill-downs opened outside the Deals grid) the
                    // edit would otherwise reach nothing. useDebouncedDealUpdate already
                    // maps both via COLUMN_TO_PROPERTY_MAP. `billing` is not a backend
                    // property, so it stays local on every mount.
                    if ((k.startsWith("custom_") || k === "description" || k === "forecastCategory") && currentDeal?.id) {
                      dealEditStore.merge(currentDeal.id, { [k]: strVal } as any);
                      // Grid pages persist via onFieldUpdate → updateCell →
                      // debounced save. At mounts without that wiring save
                      // directly, or the edit never reaches the backend.
                      if (!onFieldUpdate && !previewMode) {
                        scheduleDealFieldUpdate(currentDeal.id, k, strVal);
                      }
                    }
                    onFieldUpdate?.(k, strVal);
                  }}
                />
              </>
            )}
          </div>
        </div>

        {/* Products / Line items */}
        {productsEnabled && (
          <ProductsCollapsibleSection
            count={lineItems.length}
          >
            <DealProductsPicker
              items={lineItems}
              onChange={setLineItems}
              onTotalChange={(total) => {
                setOverrides(prev => ({ ...prev, amount: Math.round(total) }));
                persistField("amount", String(Math.round(total)));
              }}
            />
          </ProductsCollapsibleSection>
        )}

        {/* Sales methodology fields (BANT / MEDDIC / MEDDPICC / SPICED) */}
        <MethodologyGate
          dealId={currentDeal.id}
          workspaceId={workspaceId}
          isReadOnly={isReadOnly}
          isPreview={previewMode}
          customProperties={(backendDeal?.customProperties as Record<string, any>) || {}}
          onFieldUpdate={persistField}
        />

        {/* Tabs: People, Meetings, Emails, Files, Activity */}
        <div className="px-6 pt-4 pb-6">
           <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className={cn("w-full grid mb-5", callsTabEnabled ? "grid-cols-8" : "grid-cols-7")}>
                <TabsTrigger value="summary" className="text-[11px] gap-1"><Sparkles className="h-3 w-3" />{t("detailSheets.tabSummary")}</TabsTrigger>
                <TabsTrigger value="notes" className="text-[11px] gap-1"><StickyNote className="h-3 w-3" />{t("common.notes")}</TabsTrigger>
                <TabsTrigger value="tasks" className="text-[11px] gap-1"><CheckSquare className="h-3 w-3" />{t("tasks.title")}</TabsTrigger>
                <TabsTrigger value="people" className="text-[11px] gap-1"><Users className="h-3 w-3" />{t("people.title")}</TabsTrigger>
                <TabsTrigger value="meetings" className="text-[11px] gap-1"><Video className="h-3 w-3" />{t("detailSheets.tabMeetings")}</TabsTrigger>
                <TabsTrigger value="emails" className="text-[11px] gap-1"><Mail className="h-3 w-3" />{t("detailSheets.tabEmails")}</TabsTrigger>
                {callsTabEnabled && <TabsTrigger value="calls" className="text-[11px] gap-1"><Phone className="h-3 w-3" />{t("detailSheets.tabCalls")}</TabsTrigger>}
                <TabsTrigger value="files" className="text-[11px] gap-1"><Paperclip className="h-3 w-3" />{t("detailSheets.tabFiles")}</TabsTrigger>
              </TabsList>

             {/* Summary */}
             <TabsContent value="summary" className="space-y-4">
               {summaryBlocks ? (
                 <div className="space-y-3">
                   <div className="flex items-center gap-2 text-xs text-muted-foreground px-4">
                     <Sparkles className="h-3 w-3 text-accent" />
                     <span>{t("detailSheets.aiGeneratedSummary")}</span>
                   </div>
                   <NoteEditor
                     initialContent={summaryBlocks}
                     onChange={() => {}}
                     autoFocus={false}
                     editable={false}
                   />
                   {backendDeal?.asOfTs && (
                     <p className="text-[10px] text-muted-foreground pt-1 px-4">
                       {t("detailSheets.lastUpdatedAt", { timestamp: formatDateTime(backendDeal.asOfTs) })}
                     </p>
                   )}
                 </div>
               ) : (
                 <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
                   <div className="flex items-center gap-2 text-xs text-muted-foreground">
                     <Sparkles className="h-3 w-3 text-accent" />
                     <span>{t("detailSheets.aiGeneratedSummary")}</span>
                   </div>
                   {previewMode ? (
                     <div className="space-y-2 text-sm leading-relaxed">
                       <p>
                         <LinkedText
                           text={`${deal?.name} is currently in the ${deal?.stage || "pipeline"} stage at ${formatAmount(deal?.amount || 0)}.`}
                           onPersonClick={(p) => pushNav({ type: "person", data: { id: p.id, name: p.name, title: p.title, company: p.company, email: p.email, phone: p.phone } })}
                           onCompanyClick={(c) => pushNav({ type: "company", data: { id: c.id, name: c.name, industry: c.industry, description: c.description, location: c.location, website: c.website, employees: c.employees, revenue: c.revenue } })}
                         />
                       </p>
                       <p>
                         <LinkedText
                           // eslint-disable-next-line coffee-i18n/no-hardcoded-plural -- Lovable-preview dummy narrative; never rendered in production.
                           text={`There have been ${dummyMeetings.length} meetings and ${dummyEmails.length} email threads logged. The most recent interaction was an email on ${dummyEmails[0]?.date} regarding "${dummyEmails[0]?.subject}."`}
                           onPersonClick={(p) => pushNav({ type: "person", data: { id: p.id, name: p.name, title: p.title, company: p.company, email: p.email, phone: p.phone } })}
                           onCompanyClick={(c) => pushNav({ type: "company", data: { id: c.id, name: c.name, industry: c.industry, description: c.description, location: c.location, website: c.website, employees: c.employees, revenue: c.revenue } })}
                         />
                       </p>
                       <p>
                         <Trans
                           i18nKey="detailSheets.dealMomentumLine"
                           values={{ closeDate: deal?.closeDate || t("detailSheets.tbd") }}
                           components={{ positive: <span className="text-emerald-500 font-medium" /> }}
                         />
                       </p>
                     </div>
                   ) : (
                     <p className="text-muted-foreground text-xs italic">
                       {t("detailSheets.noAiSummary")}
                     </p>
                   )}
                   <p className="text-[10px] text-muted-foreground pt-1">{t("detailSheets.summaryLastUpdated")}</p>
                 </div>
               )}
              </TabsContent>

              {/* Notes */}
              <TabsContent value="notes">
                <NotesTable
                  notes={dealNotes.map(n => ({ id: n.id, name: n.title, date: n.date, type: n.type, content: n.content }))}
                  onRowClick={n => pushNav({ type: "note", data: n as NoteItem })}
                  onAdd={() => window.dispatchEvent(new CustomEvent("openAddDialog", { detail: { type: "note", association: deal ? { id: deal.id, type: "deal", name: deal.name } : undefined } }))}
                  onDelete={n => { if (previewMode) return; return deleteNote(n.id, refetchDealNotes); }}
                />
              </TabsContent>

              {/* Tasks */}
              <TabsContent value="tasks">
                <TasksTable
                  tasks={dealTasks.map(t => ({ id: t.id, title: t.title, dueDate: t.dueDate, assignee: t.assignee, completed: t.completed }))}
                  onRowClick={t => setSelectedTaskId(t.id)}
                  onAdd={() => window.dispatchEvent(new CustomEvent("openAddDialog", { detail: { type: "task", association: deal ? { id: deal.id, type: "deal", name: deal.name } : undefined } }))}
                  onDelete={t => { if (!previewMode) deleteTask(t.id); }}
                />
              </TabsContent>

             {/* People */}
             <TabsContent value="people">
              <PeopleTable
                people={dealPeople.map(p => ({ id: p.id, name: p.name, title: p.title, company: p.company, email: p.email, phone: p.phone }))}
                onRowClick={p => pushNav({ type: "person", data: p })}
                showCompany
                emptyMessage={t("detailSheets.noPeopleOnDeal")}
              />
            </TabsContent>

            {/* Meetings */}
            <TabsContent value="meetings">
              <MeetingsTable
                meetings={previewMode ? dummyMeetings : meetings}
                onRowClick={(m) => {
                  const meetingDate = new Date((m as any).startTime || m.date);
                  const isUpcoming = meetingDate > new Date();

                  if (isUpcoming) {
                    setBriefingMeeting({
                      id: m.id,
                      title: (m as any).displayName || (m as any).name || m.title || 'Meeting',
                      date: formatDate(meetingDate),
                      time: formatTime(meetingDate),
                      duration: '30 min',
                      type: 'Meeting',
                      attendees: (m as any).contacts?.map((c: any) => [c.properties?.firstName, c.properties?.lastName].filter(Boolean).join(' ') || c.primaryEmail).filter(Boolean) || m.attendees || [],
                      link: undefined,
                    });
                    setBriefingOpen(true);
                  } else {
                    setMeetingIntelligenceNote({
                      id: m.id,
                      name: (m as any).displayName || (m as any).name || m.title || 'Meeting',
                      date: (m as any).startTime || m.date,
                      people: (m as any).contacts?.map((c: any) => [c.properties?.firstName, c.properties?.lastName].filter(Boolean).join(' ') || c.primaryEmail).filter(Boolean) || m.attendees || [],
                      company: currentDeal.company || '',
                    });
                    setMeetingIntelligenceTab("summary");
                    setMeetingIntelligenceOpen(true);
                  }
                }}
                onAttendeeClick={async (name) => {
                  const allMeetings = previewMode ? dummyMeetings : meetings;
                  for (const m of allMeetings) {
                    const contact = (m as any).contacts?.find((c: any) => {
                      const fullName = `${c?.properties?.firstName || ''} ${c?.properties?.lastName || ''}`.trim();
                      return fullName === name || c?.primaryEmail === name;
                    });
                    if (contact) {
                      setSelectedPerson({
                        id: contact.id,
                        name: `${contact.properties?.firstName || ''} ${contact.properties?.lastName || ''}`.trim() || contact.primaryEmail || name,
                        title: contact.properties?.jobTitle || '',
                        company: contact.accounts?.edges?.[0]?.node?.properties?.name || (contact as any).accounts?.[0]?.properties?.name || '',
                        email: contact.primaryEmail || '',
                        phone: contact.properties?.phone || '',
                      });
                      setPersonSheetOpen(true);
                      return;
                    }
                  }
                  // Attendee isn't among the loaded meetings' contacts (e.g. an
                  // external invitee): resolve the name to a real contact via
                  // the backend in production, dummy people in preview.
                  const p = await resolvePerson(name);
                  if (p) {
                    setSelectedPerson({ id: p.id, name: p.name, title: p.title, company: p.company, email: p.email, phone: p.phone });
                    setPersonSheetOpen(true);
                  }
                }}
              />
              {!previewMode && hasMeetingMore && (
                <div className="flex justify-center py-4">
                  <Button onClick={loadMoreMeetings} variant="outline" size="sm">
                    {t("detailSheets.loadMore")}
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Emails */}
            <TabsContent value="emails">
              <EmailsTable
                emails={previewMode ? dummyEmails : emailEngagements}
                onRowClick={(e) => {
                  pushNav({
                    type: "email",
                    data: {
                      id: e.id,
                      subject: e.subject || (e as any).displayName || 'Email',
                      date: e.date || (e as any).startTime,
                      from: e.from || 'Unknown',
                      direction: e.direction || 'inbound',
                    },
                  });
                }}
                onSenderClick={async (name) => {
                  const p = await resolvePerson(name);
                  if (p) {
                    setSelectedPerson({ id: p.id, name: p.name, title: p.title, company: p.company, email: p.email, phone: p.phone });
                    setPersonSheetOpen(true);
                  }
                }}
              />
              {!previewMode && hasEmailMore && (
                <div className="flex justify-center py-4">
                  <Button onClick={loadMoreEmails} variant="outline" size="sm">
                    {t("detailSheets.loadMore")}
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Calls */}
            {callsTabEnabled && (
              <TabsContent value="calls">
                <RecordCallsTab recordType="deal" recordId={deal?.id || ''} enabled={open && !isTestData && callsTabEnabled} />
              </TabsContent>
            )}

            {/* Files */}
            <TabsContent value="files">
              <FilesTable
                files={previewMode ? dummyFiles : dealFiles}
                onRowClick={f => pushNav({ type: "file", data: f })}
                onFileUpload={uploadFile}
                uploading={uploading}
                uploadProgress={uploadProgress}
              />
            </TabsContent>

          </Tabs>
        </div>
        </>
        )}
      </SheetContent>
    </Sheet>

    {/* Meeting Intelligence Sheet - stacked on top */}
    <MeetingIntelligenceSheet
      open={meetingIntelligenceOpen}
      onOpenChange={setMeetingIntelligenceOpen}
      note={meetingIntelligenceNote}
      defaultTab={meetingIntelligenceTab}
      hideOverlay={true}
      onBack={() => setMeetingIntelligenceOpen(false)}
    />

    {/* Meeting Briefing Sheet - stacked on top */}
    <MeetingBriefingSheet
      open={briefingOpen}
      onOpenChange={setBriefingOpen}
      meeting={briefingMeeting}
      isRecording={false}
      onRecordingChange={() => {}}
      hideOverlay={true}
      onBack={() => setBriefingOpen(false)}
    />

    {/* Nested PersonDetailSheet */}
    <PersonDetailSheet
      open={personSheetOpen}
      onOpenChange={setPersonSheetOpen}
      person={selectedPerson}
      hideOverlay={true}
      onBack={() => setPersonSheetOpen(false)}
    />

    {/* CompanyDetailSheet - stacked on top */}
    <CompanyDetailSheet
      open={companySheetOpen}
      onOpenChange={setCompanySheetOpen}
      company={selectedCompany}
      hideOverlay={true}
      onBack={() => setCompanySheetOpen(false)}
    />

    {/* TaskDetailSheet - stacked on top */}
    <TaskDetailSheet
      taskId={selectedTaskId}
      open={!!selectedTaskId}
      onOpenChange={(o) => { if (!o) setSelectedTaskId(null); }}
      hideOverlay={true}
      onBack={() => setSelectedTaskId(null)}
    />

    <AddToListDialog
      open={addToListOpen}
      onOpenChange={setAddToListOpen}
      count={1}
      entityLabel="deal"
      recordIds={deal ? [deal.id] : []}
      onConfirm={(listName) => toast.success(t("detailSheets.addedToList", { name: currentDeal.name, list: listName }))}
    />
    <ConfirmDeleteDialog
      open={confirmDeleteOpen}
      onOpenChange={setConfirmDeleteOpen}
      title={`Delete ${currentDeal.name}?`}
      description={t("detailSheets.deleteDealDescription")}
      onConfirm={async () => {
        // Parent owns the delete on its home page (Deals); everywhere else
        // (global nav, stacked sheets) the sheet deletes itself so the button
        // isn't a silent no-op. deleteDeal handles RBAC, toast, and cache.
        const action = resolveSheetDeleteAction({
          id: deal?.id,
          hasParentHandler: !!onDelete,
          isPreview: isLovablePreview(),
        });
        if (action === 'parent') onDelete!(deal!.id);
        else if (action === 'self') await deleteDeal(deal!.id);
        onOpenChange(false);
      }}
    />
    </>
  );
}

function FieldRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[130px_1fr] gap-3 items-start py-1 [&_.text-sm]:text-xs">
      <div className="flex items-center gap-2 text-xs text-muted-foreground pt-0.5">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      <div className="relative min-w-0 flex items-center text-xs">{children}</div>
    </div>
  );
}
